import * as React from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton, TextFieldVariants } from '@mui/material';
import { renderHelpText } from './renderHelpText';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import HelpIcon from '@material-ui/icons/Help';
import { useState } from 'react';
import moment, { Moment } from 'moment';

export interface DateFieldProps {
    value?: string | Date | Moment;
    id?: string;
    name?: string;
    disabled?: boolean;
    readOnly?: boolean;
    invalid?: boolean;
    placeholder?: string;
    label?: React.ReactNode;
    helpText?: string;
    description?: string;
    style?: React.CSSProperties;
    inputStyle?: React.CSSProperties;
    onChange?: (value: string) => void;
    className?: string;
    variant?: TextFieldVariants;
    minDate?: string | Date | Moment;
    maxDate?: string | Date | Moment;
    format?: string;
    type?: 'date' | 'datetime';
    disableFuture?: boolean;
    disablePast?: boolean;
    views?: ('year' | 'month' | 'day')[];
    minutesStep?: number;
}

export const DateField = (props: DateFieldProps) => {
    const {
        value,
        id,
        name,
        disabled,
        readOnly,
        placeholder,
        label,
        helpText,
        description,
        invalid,
        style,
        inputStyle,
        onChange,
        className,
        variant = 'outlined',
        minDate,
        maxDate,
        format,
        type = 'date',
        disableFuture,
        disablePast,
        views,
        minutesStep,
    } = props;

    // Set default format based on type
    const defaultFormat = type === 'datetime' ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY';
    const dateFormat = format || defaultFormat;

    const [showHelp, setShowHelp] = useState(false);

    // Convert value to Moment object
    const momentValue = value ? moment(value) : null;

    // Handle date change
    const handleDateChange = (date: Moment | null) => {
        if (onChange) {
            if (date && date.isValid()) {
                onChange(type === 'datetime' ? date.format('YYYY-MM-DDTHH:mm') : date.format('YYYY-MM-DD'));
            } else {
                onChange('');
            }
        }
    };

    const commonProps = {
        value: momentValue,
        onChange: handleDateChange,
        disabled: disabled || readOnly,
        label: helpText ? (
            <div>
                {label}{' '}
                <IconButton size="small" onClick={() => setShowHelp(true)}>
                    <HelpIcon />
                </IconButton>
            </div>
        ) : (
            label
        ),
        minDate: minDate ? moment(minDate) : undefined,
        maxDate: maxDate ? moment(maxDate) : undefined,
        disableFuture,
        disablePast,
        views,
        format: dateFormat,
        slotProps: {
            textField: {
                id: id ? `${id}-input` : undefined,
                name,
                className,
                style,
                placeholder,
                helperText: description,
                error: invalid,
                variant,
                size: 'small' as const,
                fullWidth: true,
                InputProps: {
                    style: inputStyle,
                },
            },
        },
    };

    return (
        <>
            <LocalizationProvider dateAdapter={AdapterMoment}>
                {type === 'datetime' ? (
                    <DateTimePicker
                        {...commonProps}
                        ampm={false}
                        {...(minutesStep !== undefined && { timeSteps: { minutes: minutesStep } })}
                    />
                ) : (
                    <DatePicker {...commonProps} />
                )}
            </LocalizationProvider>

            {helpText?.length && (
                <Dialog open={showHelp} onClose={() => setShowHelp(false)}>
                    <DialogTitle>{label}</DialogTitle>
                    <DialogContent>{renderHelpText(helpText)}</DialogContent>
                </Dialog>
            )}
        </>
    );
};
